// components/common/Input/TextField.tsx
import { ReactElement, ChangeEvent } from 'react';
import { TextField as MuiTextField, InputAdornment, TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledTextField = styled(MuiTextField)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#252D3A',
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&:hover fieldset': {
      borderColor: '#00FFD4',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00FFD4',
      boxShadow: '0 0 15px rgba(0, 255, 212, 0.2)',
    },
    '&.Mui-error fieldset': {
      borderColor: '#FF3366',
    },
    '&.Mui-error.Mui-focused fieldset': {
      boxShadow: '0 0 15px rgba(255, 51, 102, 0.2)',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#8892A0',
    '&.Mui-focused': {
      color: '#00FFD4',
    },
    '&.Mui-error': {
      color: '#FF3366',
    },
  },
  '& .MuiOutlinedInput-input': {
    color: '#FFFFFF',
    '&::placeholder': {
      color: '#4A5568',
      opacity: 1,
    },
  },
  '& .MuiFormHelperText-root': {
    color: '#8892A0',
    '&.Mui-error': {
      color: '#FF3366',
    },
  },
  '& .MuiInputAdornment-root': {
    color: '#8892A0',
  },
}));

export type TextFieldProps = MuiTextFieldProps;

/**
 * TextField - Styled text input component
 */
export function TextField(props: TextFieldProps): ReactElement {
  return <StyledTextField variant="outlined" {...props} />;
}

export interface SearchInputProps extends Omit<MuiTextFieldProps, 'onChange' | 'value'> {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  onClear?: () => void;
}

/**
 * SearchInput - Search-specific input with icon
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search...',
  onClear,
  ...props
}: SearchInputProps): ReactElement {
  return (
    <StyledTextField
      variant="outlined"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      size="small"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </InputAdornment>
        ),
        endAdornment: value && onClear ? (
          <InputAdornment position="end">
            <button
              onClick={onClear}
              type="button"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#8892A0',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </InputAdornment>
        ) : null,
      }}
      sx={{
        minWidth: 200,
        '& .MuiOutlinedInput-root': {
          backgroundColor: '#1E2530',
        },
      }}
      {...props}
    />
  );
}

export default TextField;
